import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { useHistory, useParams } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import { IoPrintOutline } from "react-icons/io5";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import LoginUserDetailed from "../../Auth/LoginUserDetailed";
import { errorAlert, handleSuccess } from "../../Components/Alerts";
import { getUnitID } from "../../Auth/GetUnitCompany";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import DateInputDdMmYyyy from "../../Components/DateInputDdMmYyyy";
import PriceField from "../../Components/NumberFieldFormater";
import "../ReceivablesCRUD/ReceivablesForm.scss";

const getDefaultDateForFiscalYear = (fiscalYear) => {
  const todayIso = new Date().toISOString().slice(0, 10);
  const minFiscalDate = `${fiscalYear}-01-01`;
  const maxFiscalDate = `${fiscalYear}-12-31`;

  if (todayIso < minFiscalDate) return minFiscalDate;
  if (todayIso > maxFiscalDate) return maxFiscalDate;
  return todayIso;
};

const createEmptySnapshot = (defaultDateForFiscalYear) => ({
  date: defaultDateForFiscalYear || "",
  invoiceNR: "",
  debitLedgerID: "",
  debitAccountID: "",
  creditLedgerID: "",
  creditAccountID: "",
  total: "",
  coment: "",
});

function TransfersForm() {
  const history = useHistory();
  const { transactionId } = useParams();
  const isEditMode = Boolean(transactionId);

  const headers = useMemo(() => authHeader(), []);
  const email = LoginUserDetailed();
  const { fiscalYear } = useContext(FiscalYearContext);

  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [invoiceNR, setInvoiceNR] = useState("");
  const [debitLedgerID, setDebitLedgerID] = useState("");
  const [debitAccountID, setDebitAccountID] = useState("");
  const [creditLedgerID, setCreditLedgerID] = useState("");
  const [creditAccountID, setCreditAccountID] = useState("");
  const [total, setTotal] = useState("");
  const [coment, setComent] = useState("");
  const [unId, setUnID] = useState("");

  const [debitAccounts, setDebitAccounts] = useState([]);
  const [creditAccounts, setCreditAccounts] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [debitAccountField, setDebitAccountField] = useState(null);
  const [creditAccountField, setCreditAccountField] = useState(null);

  const [saving, setSaving] = useState(false);
  const [isInitializing, setIsInitializing] = useState(isEditMode);
  const [savedTransfer, setSavedTransfer] = useState(null);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [validationErrors, setValidationErrors] = useState({
    date: "",
    debitLedgerID: "",
    debitAccountID: "",
    creditLedgerID: "",
    creditAccountID: "",
    total: "",
    coment: "",
  });
  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");

  const defaultDateForFiscalYear = getDefaultDateForFiscalYear(fiscalYear);
  const [initialFormSnapshot, setInitialFormSnapshot] = useState(
    createEmptySnapshot(defaultDateForFiscalYear),
  );

  const currentFormSnapshot = {
    date: date || "",
    invoiceNR: String(invoiceNR || ""),
    debitLedgerID: String(debitLedgerID || ""),
    debitAccountID: String(debitAccountID || ""),
    creditLedgerID: String(creditLedgerID || ""),
    creditAccountID: String(creditAccountID || ""),
    total: String(total ?? ""),
    coment: String(coment || ""),
  };

  const isFormDirty =
    JSON.stringify(currentFormSnapshot) !== JSON.stringify(initialFormSnapshot);

  const hasUnsavedData = isEditMode
    ? isFormDirty
    : Boolean(
        debitLedgerID ||
          debitAccountID ||
          creditLedgerID ||
          creditAccountID ||
          total ||
          coment.trim() ||
          (date && date !== defaultDateForFiscalYear),
      );

  useEffect(() => {
    setMinDate(`${fiscalYear}-01-01`);
    setMaxDate(`${fiscalYear}-12-31`);

    setDate((previousDate) => {
      if (!previousDate) return `${fiscalYear}-01-01`;
      if (previousDate < `${fiscalYear}-01-01`) return `${fiscalYear}-01-01`;
      if (previousDate > `${fiscalYear}-12-31`) return `${fiscalYear}-12-31`;
      return previousDate;
    });
  }, [fiscalYear]);

  const markDirty = () => setSavedTransfer(null);

  const clearValidationError = (name) => {
    setValidationErrors((prev) => {
      if (!prev[name]) return prev;
      return { ...prev, [name]: "" };
    });
  };

  const fetchDebitAccounts = async (ledgerValue) => {
    if (!ledgerValue) {
      setDebitAccounts([]);
      return;
    }

    try {
      const response = await axios.get(ApiLink.API_URL + "Accounts/accountByLedger", {
        headers,
        params: { ledger: ledgerValue },
      });
      setDebitAccounts(response.data || []);
    } catch {
      errorAlert("Gabim në marrjen e llogarive debitore.");
    }
  };

  const fetchCreditAccounts = async (ledgerValue) => {
    if (!ledgerValue) {
      setCreditAccounts([]);
      return;
    }

    try {
      const response = await axios.get(ApiLink.API_URL + "Accounts/accountByLedger", {
        headers,
        params: { ledger: ledgerValue },
      });
      setCreditAccounts(response.data || []);
    } catch {
      errorAlert("Gabim në marrjen e llogarive kreditore.");
    }
  };

  const fetchLedger = async () => {
    try {
      const response = await axios.get(ApiLink.API_URL + "Ledger", { headers });
      setLedgers(response.data || []);
    } catch {
      errorAlert("Gabim në marrjen e ledgerëve.");
    }
  };

  const fetchNextInvoiceNumber = async () => {
    try {
      const response = await axios.get(ApiLink.API_URL + "Transfers/InvoiceNr", {
        headers,
        params: { fiscalYear },
      });
      setInvoiceNR(response.data?.NextInvoiceNr || "");
    } catch {
      errorAlert("Gabim në marrjen e numrit të transferit.");
    }
  };

  const fetchCompanyDetails = async () => {
    try {
      const response = await axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", {
        headers,
      });
      if (Array.isArray(response?.data) && response.data.length > 0) {
        setCompanyDetails(response.data[0]);
      }
    } catch {
      setCompanyDetails(null);
    }
  };

  useEffect(() => {
    getUnitID(headers).then(setUnID);
  }, [headers]);

  useEffect(() => {
    fetchLedger();
    fetchCompanyDetails();

    if (!isEditMode) {
      fetchNextInvoiceNumber();
      setDate(defaultDateForFiscalYear);
      setInitialFormSnapshot(createEmptySnapshot(defaultDateForFiscalYear));
      setIsInitializing(false);
    }
  }, [fiscalYear, isEditMode]);

  useEffect(() => {
    const loadEditData = async () => {
      if (!isEditMode) return;

      setIsInitializing(true);
      try {
        const response = await axios.get(`${ApiLink.API_URL}Transfers/getAllTransferData`, {
          headers,
          params: {
            fiscalYear,
            page: 0,
            pageSize: 5000,
          },
        });

        const items = response?.data?.items || [];
        const existing = items.find((item) => String(item.TransactionID) === String(transactionId));

        if (!existing) {
          errorAlert("Transferi nuk u gjet për editim.");
          history.push("/money/transfers");
          return;
        }

        const existingDate = existing.Date ? String(existing.Date).slice(0, 10) : defaultDateForFiscalYear;
        const existingInvoiceNr = String(existing.InvoiceNr || "");
        const existingDebitLedgerID = String(existing.Amount0 || "");
        const existingDebitAccountID = String(existing.AccountID || "");
        const existingCreditLedgerID = String(existing.Amount8 || "");
        const existingCreditAccountID = String(existing.PaymentOptions || "");
        const existingTotal = String(existing.Total ?? "");
        const existingComent = String(existing.Coment || "");

        setDate(existingDate);
        setInvoiceNR(existingInvoiceNr);
        setDebitLedgerID(existingDebitLedgerID);
        setDebitAccountID(existingDebitAccountID);
        setCreditLedgerID(existingCreditLedgerID);
        setCreditAccountID(existingCreditAccountID);
        setTotal(existingTotal);
        setComent(existingComent);

        await Promise.all([
          existing.Amount0 ? fetchDebitAccounts(existing.Amount0) : Promise.resolve(),
          existing.Amount8 ? fetchCreditAccounts(existing.Amount8) : Promise.resolve(),
        ]);

        setInitialFormSnapshot({
          date: existingDate,
          invoiceNR: existingInvoiceNr,
          debitLedgerID: existingDebitLedgerID,
          debitAccountID: existingDebitAccountID,
          creditLedgerID: existingCreditLedgerID,
          creditAccountID: existingCreditAccountID,
          total: existingTotal,
          coment: existingComent,
        });
      } catch {
        errorAlert("Gabim në ngarkimin e transferit për editim.");
      } finally {
        setIsInitializing(false);
      }
    };

    loadEditData();
  }, [isEditMode, transactionId, fiscalYear, headers, history, defaultDateForFiscalYear]);

  useEffect(() => {
    if (!debitAccounts.length || !debitAccountID) {
      if (!debitAccountID) setDebitAccountField(null);
      return;
    }

    const selected = debitAccounts.find((option) => option.AccountID === Number(debitAccountID));
    if (selected) {
      setDebitAccountField(selected);
    }
  }, [debitAccounts, debitAccountID]);

  useEffect(() => {
    if (!creditAccounts.length || !creditAccountID) {
      if (!creditAccountID) setCreditAccountField(null);
      return;
    }

    const selected = creditAccounts.find((option) => option.AccountID === Number(creditAccountID));
    if (selected) {
      setCreditAccountField(selected);
    }
  }, [creditAccounts, creditAccountID]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasUnsavedData) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedData]);

  const handleClose = () => {
    if (hasUnsavedData) {
      setShowConfirmClose(true);
      return;
    }
    history.push("/money/transfers");
  };

  const confirmClose = () => {
    setShowConfirmClose(false);
    history.push("/money/transfers");
  };

  const handlePrint = () => window.print();

  const formatDateDdMmYyyy = (isoDate) => {
    if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return isoDate || "";
    const [year, month, day] = isoDate.split("-");
    return `${day}.${month}.${year}`;
  };

  const formatCurrencyValue = (value) => {
    const numericValue = Number(value || 0);
    if (!Number.isFinite(numericValue)) return "0.00";
    return numericValue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSaving(true);

    const nextErrors = {
      date: "",
      debitLedgerID: "",
      debitAccountID: "",
      creditLedgerID: "",
      creditAccountID: "",
      total: "",
      coment: "",
    };

    if (!date) {
      nextErrors.date = "Data është e detyrueshme.";
    } else if (date < minDate || date > maxDate) {
      nextErrors.date = "Data duhet të jetë brenda vitit fiskal.";
    }

    if (!debitLedgerID) {
      nextErrors.debitLedgerID = "Ju lutem zgjidhni llogarinë debitore.";
    } else if (!ledgers.some((ledger) => ledger.LedgerID === Number(debitLedgerID))) {
      nextErrors.debitLedgerID = "Llogaria debitore e zgjedhur nuk është valide.";
    }

    if (!debitAccountID) {
      nextErrors.debitAccountID = "Ju lutem zgjidhni llogarinë debitore.";
    }

    if (!creditLedgerID) {
      nextErrors.creditLedgerID = "Ju lutem zgjidhni llogarinë kreditore.";
    } else if (!ledgers.some((ledger) => ledger.LedgerID === Number(creditLedgerID))) {
      nextErrors.creditLedgerID = "Llogaria kreditore e zgjedhur nuk është valide.";
    }

    if (!creditAccountID) {
      nextErrors.creditAccountID = "Ju lutem zgjidhni llogarinë kreditore.";
    }

    if (!total || Number(total) <= 0) {
      nextErrors.total = "Ju lutem plotësoni vlerë valide më të madhe se 0.";
    }

    if (!coment || !coment.trim()) {
      nextErrors.coment = "Ju lutem plotësoni komentin.";
    }

    if (Object.values(nextErrors).some(Boolean)) {
      setValidationErrors(nextErrors);
      setSaving(false);
      return;
    }

    setValidationErrors({
      date: "",
      debitLedgerID: "",
      debitAccountID: "",
      creditLedgerID: "",
      creditAccountID: "",
      total: "",
      coment: "",
    });

    const payload = {
      Date: date,
      ActionType: "TRS",
      InvoiceNr: invoiceNR,
      AccountID: debitAccountID,
      Total: total,
      Amount0: debitLedgerID,
      Amount8: creditLedgerID,
      PaymentOptions: creditAccountID,
      TransactionType: 0,
      CompanyUnitID: unId > 0 ? unId : null,
      Coment: coment,
      ...(isEditMode
        ? {
            TransactionID: Number(transactionId),
            ModifiedByUser: email,
          }
        : {
            CreatedByUser: email,
          }),
    };

    const request = isEditMode
      ? axios.put(ApiLink.API_URL + "Transfers/update-transfer", payload, { headers })
      : axios.post(ApiLink.API_URL + "Transfers/insert-transfer", payload, { headers });

    request
      .then(() => {
        const selectedDebitLedger = ledgers.find((item) => item.LedgerID === Number(debitLedgerID));
        const selectedCreditLedger = ledgers.find((item) => item.LedgerID === Number(creditLedgerID));
        const selectedDebitAccount = debitAccounts.find((item) => item.AccountID === Number(debitAccountID));
        const selectedCreditAccount = creditAccounts.find((item) => item.AccountID === Number(creditAccountID));

        setSavedTransfer({
          ...payload,
          TransferNumber: `TRS-${fiscalYear}-${invoiceNR}`,
          DebitLedgerName: selectedDebitLedger?.LedgerNameEN || "",
          CreditLedgerName: selectedCreditLedger?.LedgerNameEN || "",
          DebitAccountName: selectedDebitAccount?.AccountName || debitAccountField?.AccountName || "",
          DebitIdentification: selectedDebitAccount?.IdentificationNumber || "",
          CreditAccountName: selectedCreditAccount?.AccountName || creditAccountField?.AccountName || "",
          CreditIdentification: selectedCreditAccount?.IdentificationNumber || "",
        });

        handleSuccess(isEditMode ? "Transferi u përditësua me sukses" : "Transferi u shtua me sukses");

        if (isEditMode) {
          setSaving(false);
          setInitialFormSnapshot({
            date: date,
            invoiceNR: String(invoiceNR || ""),
            debitLedgerID: String(debitLedgerID || ""),
            debitAccountID: String(debitAccountID || ""),
            creditLedgerID: String(creditLedgerID || ""),
            creditAccountID: String(creditAccountID || ""),
            total: String(total ?? ""),
            coment: String(coment || ""),
          });
          history.push("/money/transfers");
          return;
        }

        setDate(defaultDateForFiscalYear);
        fetchNextInvoiceNumber();
        setDebitLedgerID("");
        setDebitAccountID("");
        setCreditLedgerID("");
        setCreditAccountID("");
        setComent("");
        setTotal("");
        setDebitAccountField(null);
        setCreditAccountField(null);
        setDebitAccounts([]);
        setCreditAccounts([]);
        setSaving(false);
        setInitialFormSnapshot(createEmptySnapshot(defaultDateForFiscalYear));
      })
      .catch((error) => {
        setSaving(false);
        if (error.response && error.response.status === 409) {
          errorAlert(error.response.data);
        } else {
          errorAlert("Nuk u ruajt transferi. Ju lutem provoni përsëri.");
        }
      });
  };

  const selectedDebitLedger = ledgers.find((item) => item.LedgerID === Number(debitLedgerID));
  const selectedCreditLedger = ledgers.find((item) => item.LedgerID === Number(creditLedgerID));
  const selectedDebitAccount = debitAccounts.find((item) => item.AccountID === Number(debitAccountID));
  const selectedCreditAccount = creditAccounts.find((item) => item.AccountID === Number(creditAccountID));

  const isPrintReady = Boolean(
    date && debitLedgerID && debitAccountID && creditLedgerID && creditAccountID && total && coment.trim(),
  );

  const sharedFieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#f7fbf9", minHeight: "50px" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d4e2db" },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#4E7B67" },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#4E7B67", borderWidth: "2px" },
    "& .MuiInputLabel-root": { color: "#476057" },
    "& .MuiInputBase-input": { fontSize: "14px", paddingTop: "13px", paddingBottom: "13px" },
  };

  const sharedMenuProps = {
    PaperProps: {
      sx: {
        mt: 0.5,
        border: "1px solid #dce9e2",
        borderRadius: "12px",
        boxShadow: "0 10px 24px rgba(31, 58, 54, 0.12)",
        maxHeight: 260,
        "& .MuiMenuItem-root": { fontSize: "14px", minHeight: 38 },
      },
    },
  };

  if (isInitializing) {
    return (
      <div className="RFM__page">
        <div className="RFM__screen" style={{ minHeight: "260px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "260px" }}>
            <CircularProgress size={28} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="RFM__page">
      <div className="RFM__screen print-hide">
        <div className="RFM__header">
          <div className="RFM__titleWrap">
            <h1 className="RFM__title">{isEditMode ? "Përditëso Transferin" : "Transfer i ri"}</h1>
            <p className="RFM__subtitle">
              {isEditMode
                ? "Përditëso fushat kryesore dhe ruaj ndryshimet."
                : "Plotëso fushat kryesore dhe ruaj transferin."}
            </p>
          </div>
          <div className="RFM__headerActions">
            <button type="button" className="RFM__btn RFM__btn--light" onClick={handlePrint} disabled={saving || !isPrintReady}>
              <IoPrintOutline />
              Printo Mandatin
            </button>
            <button type="submit" form="transfers-form" className="RFM__btn RFM__btn--primary" disabled={saving || !hasUnsavedData}>
              {saving ? (
                <>
                  <CircularProgress style={{ color: "white", width: 18, height: 18 }} />
                  Duke ruajtur...
                </>
              ) : (
                <>{isEditMode ? "Përditëso" : "Ruaj & Ri"}</>
              )}
            </button>
            <button type="button" className="RFM__btn RFM__btn--light" onClick={handleClose}>
              <FaTimes />
              Mbyll
            </button>
          </div>
        </div>

        <div className="RFM__card">
          <div className="RFM__cardHeader">
            <div>
              <h2>{isEditMode ? "Përditësimi i Transferit" : "Regjistrimi i Transferit"}</h2>
              <p>
                {isEditMode
                  ? "Përditësoni të dhënat e kërkuara dhe ruani ndryshimet."
                  : "Plotësoni të dhënat e kërkuara për regjistrim të saktë dhe të plotë."}
              </p>
            </div>
            <div className="RFM__transferNo">TRS-{fiscalYear}-{invoiceNR || "..."}</div>
          </div>

          <form id="transfers-form" className="RFM__form" onSubmit={handleSubmit} noValidate>
            <div className="RFM__grid RFM__grid--general">
              <div className="RFM__field RFM__field--invoice">
                <TextField label="Numri i Transferit" value={`TRS-${fiscalYear}-${invoiceNR || ""}`} disabled fullWidth />
              </div>

              <div className="RFM__field RFM__field--date">
                <DateInputDdMmYyyy
                  label="Data"
                  value={date}
                  minDate={minDate}
                  maxDate={maxDate}
                  onChange={(nextIsoDate) => {
                    setDate(nextIsoDate);
                    clearValidationError("date");
                    markDirty();
                  }}
                />
                {validationErrors.date ? <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 6 }}>{validationErrors.date}</div> : null}
              </div>

              <div className="RFM__field RFM__field--amount">
                <PriceField
                  label="Vlera"
                  name="total"
                  value={total}
                  onChange={(event) => {
                    setTotal(event.target.value ?? "");
                    clearValidationError("total");
                    markDirty();
                  }}
                  decimalScale={2}
                />
                {validationErrors.total ? <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 6 }}>{validationErrors.total}</div> : null}
              </div>
            </div>

            <div className="RFM__separator" />

            <div className="RFM__grid RFM__grid--accounts">
              <section className="RFM__panel RFM__panel--debtor">
                <h3>Debitore (Destinacioni)</h3>

                <TextField
                  select
                  label="Llogaria kontabël Debitore"
                  value={debitLedgerID}
                  onChange={(event) => {
                    setDebitLedgerID(event.target.value);
                    setDebitAccountID("");
                    setDebitAccountField(null);
                    fetchDebitAccounts(event.target.value);
                    clearValidationError("debitLedgerID");
                    clearValidationError("debitAccountID");
                    markDirty();
                  }}
                  fullWidth
                  size="small"
                  error={Boolean(validationErrors.debitLedgerID)}
                  helperText={validationErrors.debitLedgerID || ""}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ MenuProps: sharedMenuProps }}
                  sx={sharedFieldSx}
                >
                  {ledgers.map((ledger) => (
                    <MenuItem key={ledger.LedgerID} value={ledger.LedgerID}>
                      {ledger.LedgerNameEN}
                    </MenuItem>
                  ))}
                </TextField>

                <Autocomplete
                  fullWidth
                  options={debitAccounts}
                  value={debitAccountField}
                  onChange={(event, value) => {
                    if (value && value.AccountID) {
                      setDebitAccountID(value.AccountID);
                      setDebitAccountField(value);
                      clearValidationError("debitAccountID");
                      markDirty();
                    } else {
                      setDebitAccountID("");
                      setDebitAccountField(null);
                      markDirty();
                    }
                  }}
                  isOptionEqualToValue={(option, value) => option.AccountID === value?.AccountID}
                  getOptionLabel={(option) => `${option?.IdentificationNumber || ""} - ${option?.AccountName || ""}`}
                  ListboxProps={{ style: { maxHeight: 220, overflow: "auto", paddingTop: 2, paddingBottom: 2 } }}
                  renderOption={(props, option) => (
                    <li {...props} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #edf3ef", borderRadius: "8px", marginBottom: "2px", padding: "9px 10px", backgroundColor: "#ffffff" }}>
                      <div>
                        <div style={{ fontSize: 12, color: "#5f6f68", letterSpacing: "0.1px" }}>{option.IdentificationNumber || "Pa ID"}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#1f3a36" }}>{option.AccountName}</div>
                      </div>
                      <span style={{ color: "#7a9388", fontSize: 14, fontWeight: 700 }}>›</span>
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label="Kërko Llogarinë Debitore" size="small" error={Boolean(validationErrors.debitAccountID)} helperText={validationErrors.debitAccountID || ""} InputLabelProps={{ shrink: true }} sx={sharedFieldSx} />}
                />
              </section>

              <section className="RFM__panel RFM__panel--creditor">
                <h3>Kreditore (Burimi)</h3>

                <TextField
                  select
                  label="Llogaria kontabël Kreditore"
                  value={creditLedgerID}
                  onChange={(event) => {
                    setCreditLedgerID(event.target.value);
                    setCreditAccountID("");
                    setCreditAccountField(null);
                    fetchCreditAccounts(event.target.value);
                    clearValidationError("creditLedgerID");
                    clearValidationError("creditAccountID");
                    markDirty();
                  }}
                  fullWidth
                  size="small"
                  error={Boolean(validationErrors.creditLedgerID)}
                  helperText={validationErrors.creditLedgerID || ""}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ MenuProps: sharedMenuProps }}
                  sx={sharedFieldSx}
                >
                  {ledgers.map((ledger) => (
                    <MenuItem key={ledger.LedgerID} value={ledger.LedgerID}>
                      {ledger.LedgerNameEN}
                    </MenuItem>
                  ))}
                </TextField>

                <Autocomplete
                  fullWidth
                  options={creditAccounts}
                  value={creditAccountField}
                  onChange={(event, value) => {
                    if (value && value.AccountID) {
                      setCreditAccountID(value.AccountID);
                      setCreditAccountField(value);
                      clearValidationError("creditAccountID");
                      markDirty();
                    } else {
                      setCreditAccountID("");
                      setCreditAccountField(null);
                      markDirty();
                    }
                  }}
                  isOptionEqualToValue={(option, value) => option.AccountID === value?.AccountID}
                  getOptionLabel={(option) => `${option?.IdentificationNumber || ""} - ${option?.AccountName || ""}`}
                  ListboxProps={{ style: { maxHeight: 220, overflow: "auto", paddingTop: 2, paddingBottom: 2 } }}
                  renderOption={(props, option) => (
                    <li {...props} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #edf3ef", borderRadius: "8px", marginBottom: "2px", padding: "9px 10px", backgroundColor: "#ffffff" }}>
                      <div>
                        <div style={{ fontSize: 12, color: "#5f6f68", letterSpacing: "0.1px" }}>{option.IdentificationNumber || "Pa ID"}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#1f3a36" }}>{option.AccountName}</div>
                      </div>
                      <span style={{ color: "#7a9388", fontSize: 14, fontWeight: 700 }}>›</span>
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label="Kërko Llogarinë Kreditore" size="small" error={Boolean(validationErrors.creditAccountID)} helperText={validationErrors.creditAccountID || ""} InputLabelProps={{ shrink: true }} sx={sharedFieldSx} />}
                />
              </section>
            </div>

            <div className="RFM__separator" />

            <div className="RFM__grid RFM__grid--general">
              <div className="RFM__field RFM__field--comment">
                <TextField
                  label="Komenti"
                  value={coment}
                  onChange={(event) => {
                    setComent(event.target.value);
                    clearValidationError("coment");
                    markDirty();
                  }}
                  multiline
                  minRows={3}
                  error={Boolean(validationErrors.coment)}
                  helperText={validationErrors.coment || ""}
                  placeholder="Shkruaj përshkrimin e transferit..."
                  fullWidth
                />
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="RFM__print print-only">
        <div className="RFM__printDoc">
          <div className="RFM__printHeader">
            <div className="RFM__printBrand">
              {companyDetails?.CompanyLogo ? (
                <div className="RFM__printLogoWrap">
                  <img src={ApiLink.PHOTO_URL + companyDetails.CompanyLogo} alt="Company logo" className="RFM__printLogo" />
                </div>
              ) : null}
              <h2>{companyDetails?.CompanyName || "Kompania"}</h2>
              <p>{companyDetails?.Address || "-"}</p>
              <div className="RFM__printCompanyMeta">
                <span>NUI: {companyDetails?.IdentificationNumber || "-"}</span>
                <span>Telefon: {companyDetails?.PhoneNumbers || "-"}</span>
              </div>
            </div>
            <div className="RFM__printTitleBox">
              <h1>MANDAT TRANSFERI</h1>
              <p>Dokument financiar</p>
            </div>
          </div>

          <div className="RFM__printMetaStrip">
            <div className="RFM__printMetaItem">
              <span>Nr. dokumentit</span>
              <strong className="RFM__mono">{savedTransfer?.TransferNumber || `TRS-${fiscalYear}-${invoiceNR}`}</strong>
            </div>
            <div className="RFM__printMetaItem">
              <span>Data e dokumentit</span>
              <strong>{formatDateDdMmYyyy(savedTransfer?.Date || date)}</strong>
            </div>
            <div className="RFM__printMetaItem">
              <span>Viti fiskal</span>
              <strong>{fiscalYear}</strong>
            </div>
            <div className="RFM__printMetaItem">
              <span>Data e printimit</span>
              <strong>{formatDateDdMmYyyy(new Date().toISOString().slice(0, 10))}</strong>
            </div>
          </div>

          <div className="RFM__printSectionTitle">Palët e Transaksionit</div>
          <div className="RFM__printPartyGrid">
            <div className="RFM__printPartyCard">
              <h3>Debitore (Destinacioni)</h3>
              <div className="RFM__printPartyRow">
                <span>Llogaria kontabël</span>
                <strong>{savedTransfer?.DebitLedgerName || selectedDebitLedger?.LedgerNameEN || "-"}</strong>
              </div>
              <div className="RFM__printPartyRow">
                <span>Llogaria debitore</span>
                <strong>{savedTransfer?.DebitAccountName || selectedDebitAccount?.AccountName || debitAccountField?.AccountName || "-"}</strong>
              </div>
              <div className="RFM__printPartyRow">
                <span>Nr. identifikues</span>
                <strong>{savedTransfer?.DebitIdentification || selectedDebitAccount?.IdentificationNumber || "-"}</strong>
              </div>
            </div>

            <div className="RFM__printPartyCard">
              <h3>Kreditore (Burimi)</h3>
              <div className="RFM__printPartyRow">
                <span>Llogaria kontabël</span>
                <strong>{savedTransfer?.CreditLedgerName || selectedCreditLedger?.LedgerNameEN || "-"}</strong>
              </div>
              <div className="RFM__printPartyRow">
                <span>Llogaria kreditore</span>
                <strong>{savedTransfer?.CreditAccountName || selectedCreditAccount?.AccountName || creditAccountField?.AccountName || "-"}</strong>
              </div>
              <div className="RFM__printPartyRow">
                <span>Nr. identifikues</span>
                <strong>{savedTransfer?.CreditIdentification || selectedCreditAccount?.IdentificationNumber || "-"}</strong>
              </div>
            </div>
          </div>

          <div className="RFM__printSectionTitle">Detajet Financiare</div>
          <div className="RFM__printTable">
            <div className="RFM__printTableRow">
              <span>Vlera totale e transferit</span>
              <strong>{formatCurrencyValue(savedTransfer?.Total || total)} EUR</strong>
            </div>
          </div>

          <div className="RFM__printSectionTitle">Përshkrimi</div>
          <div className="RFM__printCommentBox RFM__printCommentBox--doc">
            {savedTransfer?.Coment || coment || "-"}
          </div>

          <div className="RFM__printSignatureRow">
            <div className="RFM__printSignature">
              <span>Përpiloi</span>
              <div className="RFM__printSignatureLine" />
            </div>
            <div className="RFM__printSignature">
              <span>Pranoi</span>
              <div className="RFM__printSignatureLine" />
            </div>
          </div>

          <div className="RFM__printFooterNote">Dokument i gjeneruar elektronikisht dhe i vlefshëm për evidencë kontabël.</div>
          <div className="RFM__printCopyright">Copyright © {new Date().getFullYear()} Accountree. Të gjitha të drejtat e rezervuara.</div>
        </div>
      </div>

      {showConfirmClose && (
        <div className="RFM__modalOverlay">
          <div className="RFM__modal">
            <h3>Kujdes</h3>
            <p>Keni ndryshime të paruajtura. A dëshironi të mbyllet forma pa ruajtur?</p>
            <div className="RFM__modalActions">
              <button type="button" className="RFM__btn RFM__btn--light" onClick={() => setShowConfirmClose(false)}>
                Jo, qëndro
              </button>
              <button type="button" className="RFM__btn RFM__btn--danger" onClick={confirmClose}>
                Po, mbylle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransfersForm;
